import cognitoService from './cognitoService';

/**
 * Download a PDF file from S3 using the downloader Lambda
 * @param {string} pdfKey - The S3 key/path to the PDF file
 * @param {string} userId - Optional user ID (will get from auth if not provided)
 * @param {string} token - Optional authentication token (will get from auth if not provided)
 * @param {string} bucketName - Optional bucket name (uses default if not provided)
 * @returns {Promise<void>} - Triggers browser download
 */
export const downloadPDF = async (pdfKey, userId = null, token = null, bucketName = null) => {
  try {
    console.log(`Initiating download for: ${pdfKey}`);
    
    // Get authentication info from CognitoService if not provided
    if (!token || !userId) {
      const userResult = await cognitoService.getCurrentUser();
      if (!userResult.success) {
        throw new Error('Authentication required. Please log in again.');
      }
      
      userId = userId || userResult.user.userId;
      token = token || cognitoService.getIdToken();
    }
    
    if (!token) {
      throw new Error('Authentication token not found. Please log in again.');
    }
    
    // Prepare request parameters
    const params = new URLSearchParams({
      key: pdfKey,
      userId: userId
    });
    
    if (bucketName) {
      params.append('bucket', bucketName);
    }
    
    // Call the downloader Lambda
    const apiEndpoint = import.meta.env.VITE_API_ENDPOINT || import.meta.env.REACT_APP_API_ENDPOINT;
    const response = await fetch(`${apiEndpoint}/download?${params.toString()}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      mode: 'cors'
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(`Download failed: ${errorData.error || response.statusText}`);
    }
    
    const result = await response.json();
    
    if (!result.success) {
      throw new Error(`Download failed: ${result.error}`);
    }
    
    console.log('Download URL generated successfully:', result.objectInfo);
    
    // Use the pre-signed URL to download the file
    // This approach works without CORS issues since the download URL is direct from S3
    const downloadUrl = result.downloadUrl;
    const filename = pdfKey.split('/').pop(); // Extract filename from key
    
    // Option 1: Direct browser download (recommended)
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = filename;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    // Option 2: Alternative - open in new tab
    // window.open(downloadUrl, '_blank');
    
    console.log(`Download initiated for ${filename}`);
    
    return {
      success: true,
      filename: filename,
      size: result.objectInfo.size,
      downloadUrl: downloadUrl,
      expiresAt: result.expiresAt
    };
    
  } catch (error) {
    console.error('Download error:', error);
    
    // Handle specific error cases
    if (error.message.includes('Authentication')) {
      // Redirect to root to trigger the Cognito login flow
      window.location.href = '/';
      return;
    }
    
    if (error.message.includes('Access denied')) {
      alert('You do not have permission to download this file.');
      return;
    }
    
    if (error.message.includes('not found')) {
      alert('The requested file could not be found. It may have been deleted or moved.');
      return;
    }
    
    // Generic error handling
    alert(`Download failed: ${error.message}`);
    throw error;
  }
};

/**
 * Download multiple PDFs as a batch
 * @param {Array} pdfList - Array of {key, userId, token, bucketName} objects
 * @returns {Promise<Array>} - Array of download results
 */
export const downloadMultiplePDFs = async (pdfList) => {
  const results = [];
  
  for (const pdf of pdfList) {
    try {
      const result = await downloadPDF(pdf.key, pdf.userId, pdf.token, pdf.bucketName);
      results.push({ ...pdf, success: true, result });
      
      // Add small delay between downloads to avoid overwhelming the browser
      await new Promise(resolve => setTimeout(resolve, 500));
      
    } catch (error) {
      console.error(`Failed to download ${pdf.key}:`, error);
      results.push({ ...pdf, success: false, error: error.message });
    }
  }
  
  return results;
};

/**
 * Get a presigned S3 URL without triggering a download (useful for preview)
 * @param {string} pdfKey - The S3 key/path to the PDF file
 * @param {string} userId - Optional user ID (will get from auth if not provided)
 * @param {string} token - Optional authentication token (will get from auth if not provided)
 * @param {string} bucketName - Optional bucket name
 * @returns {Promise<Object>} - Object with presigned download URL and metadata
 */
export const getPresignedURL = async (pdfKey, userId = null, token = null, bucketName = null) => {
  try {
    // Get authentication info from CognitoService if not provided
    if (!token || !userId) {
      const userResult = await cognitoService.getCurrentUser();
      if (!userResult.success) {
        throw new Error('Authentication required. Please log in again.');
      }

      userId = userId || userResult.user.userId;
      token = token || cognitoService.getIdToken();
    }

    if (!token) {
      throw new Error('Authentication token not found. Please log in again.');
    }

    const params = new URLSearchParams({
      key: pdfKey,
      userId: userId
    });

    if (bucketName) {
      params.append('bucket', bucketName);
    }

    const apiEndpoint = import.meta.env.VITE_API_ENDPOINT || import.meta.env.REACT_APP_API_ENDPOINT;
    const response = await fetch(`${apiEndpoint}/download?${params.toString()}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      mode: 'cors'
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(`Failed to get presigned URL: ${errorData.error || response.statusText}`);
    }

    const result = await response.json();

    if (!result.success) {
      throw new Error(`Failed to get presigned URL: ${result.error}`);
    }

    return {
      downloadUrl: result.downloadUrl,
      expiresAt: result.expiresAt,
      objectInfo: result.objectInfo
    };

  } catch (error) {
    console.error('Failed to get presigned URL:', error);
    throw error;
  }
};

/**
 * Get download URL without triggering download (useful for preview)
 * @param {string} pdfKey - The S3 key/path to the PDF file
 * @param {string} userId - Optional user ID (will get from auth if not provided)
 * @param {string} token - Optional authentication token (will get from auth if not provided)
 * @param {string} bucketName - Optional bucket name
 * @returns {Promise<Object>} - Object with download URL and metadata
 * @deprecated Use getPresignedURL instead
 */
export const getDownloadURL = async (pdfKey, userId = null, token = null, bucketName = null) => {
  return getPresignedURL(pdfKey, userId, token, bucketName);
};